import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { DateTime } from 'luxon';
import { useDebounce } from '@uidotdev/usehooks';
import { styled } from '@mui/material/styles';
import {
  Box,
  Select,
  SelectChangeEvent,
  MenuItem,
  Paper,
  InputLabel,
  FormControl,
} from '@mui/material';
import {
  GridCallbackDetails,
  GridPaginationModel,
  GridSortModel,
  useGridApiRef,
} from '@mui/x-data-grid-pro';
import {
  DataGridHeader,
  DataGridTitle,
  DataGridFilters,
  DataGridSearch,
  DataGridFilterMenu,
  type DataGridFilter,
} from '../../tables/DataGridPro';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../hooks/useAppSelector';

import { TripLeg } from '../../../data/models/reservation.model';
import {
  tripStatusUnallocatedList,
  tripStatusAllocatedList,
  tripStatusInProgressList,
  tripStatusUnattestedList,
  tripStatusInactiveList,
  tripStatusListToEventMap,
  TripLegStatusBC,
} from '../../../data/models/trip-leg-status.model';
import { SortModel } from '../../../data/models/sort-model.model';

import { TripLegEventType } from '../../../services/webSocketService/updateTripLeg';

import * as lookupSlice from '../../../data/lookup.slice';
import * as triplegSlice from '../../../data/tripleg.slice';
import * as providerSlice from '../../../data/provider.slice';

import { TripLegInventoryTable } from './TripLegInventoryTable';
import TripLegPackageDialog from './TripLegPackageDialog';

const PREFIX = 'TripLegInventory';

const classes = {
  container: `${PREFIX}-container`,
  searchContainer: `${PREFIX}-searchContainer`,
  filterContainer: `${PREFIX}-filterContainer`,
  datePickerContainer: `${PREFIX}-datePickerContainer`,
};

type TripLegFilters =
  | 'allocated'
  | 'unallocated'
  | 'inprogress'
  | 'unattested'
  | 'inactive'
  | 'showAll';

const tripLegFilterToStatusListMap = {
  allocated: tripStatusListToEventMap['tripStatusAllocatedList'],
  unallocated: tripStatusListToEventMap['tripStatusUnallocatedList'],
  inprogress: tripStatusListToEventMap['tripStatusInProgressList'],
  unattested: tripStatusListToEventMap['tripStatusUnattestedList'],
  inactive: tripStatusListToEventMap['tripStatusInactiveList'],
};

const StyledBox = styled(Paper)(({ theme }) => ({
  [`& .${classes.container}`]: {
    paddingTop: '24px',
    paddingRight: '32px',
    paddingLeft: '32px',
  },
  [`& .${classes.searchContainer}`]: {
    width: '327px',
  },
  [`& .${classes.filterContainer}`]: {
    maxWidth: '249px',
  },
  [`& .${classes.datePickerContainer}`]: {
    width: '200px',
  },
}));

export default function TripLegInventory() {
  const { t } = useTranslation('common');
  const dispatch = useAppDispatch();
  const dataGridApiRef = useGridApiRef();

  const providerFilterParams = 'ctt0&active0'; // only get cleared and active providers

  const allTripLegs = useAppSelector(triplegSlice.selectAllTripLegs);
  const allTripLegsLoading = useAppSelector(
    triplegSlice.selectLoadingAllTripLegs,
  );
  const page = useAppSelector(triplegSlice.selectInventoryPage);
  const pageSize = useAppSelector(triplegSlice.selectInventoryPageSize);
  const sortModel = useAppSelector(triplegSlice.selectInventoryPageSortModel);
  const savedTripLeg = useAppSelector(triplegSlice.selectSavedTripLeg);
  const tripLegEventEmitted = useAppSelector(
    triplegSlice.selectTripLegEventEmitted,
  );
  const passengerCareLevelBCs = useAppSelector(
    lookupSlice.selectPassengerCareLevels,
  );
  const tripModeBCs = useAppSelector(lookupSlice.selectTripModes);
  const providers = useAppSelector(providerSlice.selectProviderList);

  const [searchTerm, setSearchTerm] = useState<string>('');
  const [tripLegFilter, setTripLegFilter] =
    useState<TripLegFilters>('unallocated');
  const [isSelectingTrips, setIsSelectingTrips] = useState(false);
  const [selectedTripLegs, setSelectedTripLegs] = useState<TripLeg[]>([]);
  const [dialogPackageOpen, setDialogPackageOpen] = useState(false);
  const [filteredTripLegs, setFilteredTripLegs] = useState<TripLeg[]>([]);
  const [filterDate, setFilterDate] = useState<DateTime | null>(null);

  const { control, setValue, watch, resetField, formState } = useForm({
    mode: 'onTouched',
    defaultValues: {
      tripStatus: [],
      pickUpDate: {
        from: null,
        to: null,
      },
      provider: '',
      memberName: '',
      address: '',
      tripMode: [],
      passengerCareLevel: [],
    },
  });

  // Watch form value changes
  const filterValues = watch();
  const filterTripStatus = watch('tripStatus');
  const filterPickUpDateFrom = watch('pickUpDate.from');
  const filterPickUpDateTo = watch('pickUpDate.to');
  const filterProvider = watch('provider');
  const filterMemberName = watch('memberName');
  const filterAddress = watch('address');
  const filterTripMode = watch('tripMode');
  const filterPassengerCareLevel = watch('passengerCareLevel');

  // Debounce the form value updates to prevent excessive API calls
  const debouncedFilterTripStatus = useDebounce(filterTripStatus, 500);
  const debouncedFilterPickUpDateFrom = useDebounce(filterPickUpDateFrom, 500);
  const debouncedFilterPickUpDateTo = useDebounce(filterPickUpDateTo, 500);
  const debouncedFilterProvider = useDebounce(filterProvider, 500);
  const debouncedFilterMemberName = useDebounce(filterMemberName, 500);
  const debouncedFilterAddress = useDebounce(filterAddress, 500);
  const debouncedFilterTripMode = useDebounce(filterTripMode, 500);
  const debouncedFilterPassengerCareLevel = useDebounce(
    filterPassengerCareLevel,
    500,
  );

  // Define the filters used in the DataGridFilterMenu
  const filters: DataGridFilter[] = [
    {
      label: t('tripLegFilters.tripStatus'),
      name: 'tripStatus',
      value: 'All',
      type: 'multi-select',
      options: [
        ...Object.values(TripLegStatusBC).map((status) => ({
          label: status,
          value: status,
        })),
      ],
    },
    {
      label: t('tripLegFilters.pickUpDate'),
      name: 'pickUpDate',
      type: 'date-range',
    },
    {
      label: t('tripLegFilters.provider'),
      name: 'provider',
      type: 'text',
    },
    {
      label: t('tripLegFilters.memberName'),
      name: 'memberName',
      type: 'text',
    },
    {
      label: t('tripLegFilters.address'),
      name: 'address',
      type: 'text',
    },
    {
      label: t('tripLegFilters.tripMode'),
      name: 'tripMode',
      type: 'multi-select',
      options: tripModeBCs?.map((mode) => ({
        label: `${mode.tripModeName} (${mode.tripModeBC})`,
        value: mode.tripModeBC,
      })),
    },
    {
      label: t('tripLegFilters.tripLevelOfCare'),
      name: 'passengerCareLevel',
      type: 'multi-select',
      options: passengerCareLevelBCs?.map((level) => ({
        label: `${level.passengerCareLevelName} (${level.passengerCareLevelBC})`,
        value: level.passengerCareLevelBC,
      })),
    },
  ];

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
  };

  const handleTripLegFilterChange = (event: SelectChangeEvent) => {
    setIsSelectingTrips(event.target.value === 'unallocated');
    setSelectedTripLegs([]);
    setTripLegFilter(event.target.value as TripLegFilters);
  };

  const closePackageDialog = () => {
    setDialogPackageOpen(false);
    clearRowSelection();
  };

  const getSelectedRowIDsFromDataGrid = () => {
    return Array.from(dataGridApiRef.current.getSelectedRows()).map(
      ([key]) => key,
    );
  };

  const getSelectedTripLegsFromDataGrid = (): TripLeg[] => {
    return Array.from(dataGridApiRef.current.getSelectedRows()).map(
      ([key, value]) => value,
    ) as TripLeg[];
  };

  const clearRowSelection = () => {
    dataGridApiRef.current.selectRows(
      getSelectedRowIDsFromDataGrid(),
      false,
      true,
    );
  };

  const filterTripLegs = (items: TripLeg[]) => {
    let filteredItems = items;

    if (tripLegFilter === 'inprogress') {
      filteredItems = filteredItems.filter(
        (tripLeg) =>
          !tripStatusInactiveList.some(
            (status) =>
              tripLeg.tripLegStatusBC?.toLowerCase() === status.toLowerCase(),
          ),
      );
    }

    return filteredItems;
  };

  const fetchTripLegs = () => {
    const appointmentStartDate =
      debouncedFilterPickUpDateFrom && debouncedFilterPickUpDateTo
        ? debouncedFilterPickUpDateFrom
        : filterDate?.toISODate() ?? undefined;
    const appointmentEndDate =
      debouncedFilterPickUpDateFrom && debouncedFilterPickUpDateTo
        ? debouncedFilterPickUpDateTo
        : filterDate?.toISODate() ?? undefined;
    const passengerCareLevelBCs = filterPassengerCareLevel?.length
      ? filterPassengerCareLevel
      : undefined;
    const tripModeBCs = filterTripMode?.length ? filterTripMode : undefined;

    if (formState.isDirty && debouncedFilterTripStatus?.length) {
      dispatch(
        triplegSlice.fetchAllTripLegsAsync({
          page,
          pageSize,
          sortModel: sortModel ?? undefined,
          tripLegStatusBCs: debouncedFilterTripStatus,
          search: searchTerm || undefined,
          includeInvalid: false,
          appointmentStartDate,
          appointmentEndDate,
          searchAddress: filterAddress || undefined,
          searchProviderName: filterProvider || undefined,
          passengerCareLevelBCs,
          searchMemberName: filterMemberName || undefined,
          tripModeBCs,
        }),
      );
      // If statuses are being filtered on, return early here to avoid
      // tripLegFilter configuration
      return;
    }

    switch (tripLegFilter) {
      case 'unallocated':
        dispatch(
          triplegSlice.fetchAllTripLegsAsync({
            page,
            pageSize,
            sortModel: sortModel ?? undefined,
            tripLegStatusBCs: debouncedFilterTripStatus?.length
              ? debouncedFilterTripStatus
              : tripStatusUnallocatedList.map((status) =>
                  status.toString().toLocaleUpperCase(),
                ),
            providerAssigned: false,
            search: searchTerm || undefined,
            includeInvalid: false,
            appointmentStartDate,
            appointmentEndDate,
            searchAddress: filterAddress || undefined,
            searchProviderName: filterProvider || undefined,
            passengerCareLevelBCs,
            searchMemberName: filterMemberName || undefined,
            tripModeBCs,
          }),
        );
        break;
      case 'allocated':
        dispatch(
          triplegSlice.fetchAllTripLegsAsync({
            page,
            pageSize,
            sortModel: sortModel ?? undefined,
            tripLegStatusBCs: debouncedFilterTripStatus?.length
              ? debouncedFilterTripStatus
              : tripStatusAllocatedList.map((status) =>
                  status.toString().toLocaleUpperCase(),
                ),
            providerAssigned: true,
            search: searchTerm || undefined,
            includeInvalid: false,
            appointmentStartDate,
            appointmentEndDate,
            searchAddress: filterAddress || undefined,
            searchProviderName: filterProvider || undefined,
            passengerCareLevelBCs,
            searchMemberName: filterMemberName || undefined,
            tripModeBCs,
          }),
        );
        break;
      case 'inprogress':
        dispatch(
          triplegSlice.fetchAllTripLegsAsync({
            page,
            pageSize,
            sortModel: sortModel ?? undefined,
            tripLegStatusBCs: debouncedFilterTripStatus?.length
              ? debouncedFilterTripStatus
              : tripStatusInProgressList.map((status) =>
                  status.toString().toLocaleUpperCase(),
                ),
            search: searchTerm || undefined,
            includeInvalid: false,
            appointmentStartDate,
            appointmentEndDate,
            searchAddress: filterAddress || undefined,
            searchProviderName: filterProvider || undefined,
            passengerCareLevelBCs,
            searchMemberName: filterMemberName || undefined,
            tripModeBCs,
          }),
        );
        break;
      case 'unattested':
        dispatch(
          triplegSlice.fetchAllTripLegsAsync({
            page,
            pageSize,
            sortModel: sortModel ?? undefined,
            tripLegStatusBCs: debouncedFilterTripStatus?.length
              ? debouncedFilterTripStatus
              : tripStatusUnattestedList.map((status) =>
                  status.toString().toLocaleUpperCase(),
                ),
            // unattestedOnly: true,
            search: searchTerm || undefined,
            includeInvalid: false,
            appointmentStartDate,
            appointmentEndDate,
            searchAddress: filterAddress || undefined,
            searchProviderName: filterProvider || undefined,
            passengerCareLevelBCs,
            searchMemberName: filterMemberName || undefined,
            tripModeBCs,
          }),
        );
        break;
      case 'inactive':
        dispatch(
          triplegSlice.fetchAllTripLegsAsync({
            page,
            pageSize,
            sortModel: sortModel ?? undefined,
            tripLegStatusBCs: debouncedFilterTripStatus?.length
              ? debouncedFilterTripStatus
              : tripStatusInactiveList.map((status) =>
                  status.toString().toLocaleUpperCase(),
                ),
            search: searchTerm || undefined,
            includeInvalid: false,
            appointmentStartDate,
            appointmentEndDate,
            searchAddress: filterAddress || undefined,
            searchProviderName: filterProvider || undefined,
            passengerCareLevelBCs,
            searchMemberName: filterMemberName || undefined,
            tripModeBCs,
          }),
        );
        break;
      case 'showAll':
        dispatch(
          triplegSlice.fetchAllTripLegsAsync({
            page,
            pageSize,
            sortModel: sortModel ?? undefined,
            tripLegStatusBCs: debouncedFilterTripStatus?.length
              ? debouncedFilterTripStatus
              : Object.values(TripLegStatusBC).map((status) =>
                  status.toString().toLocaleUpperCase(),
                ),
            search: searchTerm || undefined,
            includeInvalid: false,
            appointmentStartDate,
            appointmentEndDate,
            searchAddress: filterAddress || undefined,
            searchProviderName: filterProvider || undefined,
            passengerCareLevelBCs,
            searchMemberName: filterMemberName || undefined,
            tripModeBCs,
          }),
        );
        break;
    }
  };

  useEffect(() => {
    fetchTripLegs();
  }, [
    tripLegFilter,
    page,
    pageSize,
    sortModel,
    searchTerm,
    filterDate,
    debouncedFilterTripStatus,
    debouncedFilterProvider,
    debouncedFilterMemberName,
    debouncedFilterAddress,
    debouncedFilterTripMode,
    debouncedFilterPassengerCareLevel,
  ]);

  useEffect(() => {
    if (
      (debouncedFilterPickUpDateFrom && debouncedFilterPickUpDateTo) ||
      (!debouncedFilterPickUpDateFrom && !debouncedFilterPickUpDateTo)
    ) {
      fetchTripLegs();
    }
  }, [debouncedFilterPickUpDateFrom, debouncedFilterPickUpDateTo]);

  useEffect(() => {
    setFilteredTripLegs(
      filterTripLegs(allTripLegs.items as unknown as TripLeg[]),
    );
  }, [allTripLegs, tripLegFilter, savedTripLeg]);

  useEffect(() => {
    const handleRowSelectionChange = () => {
      setSelectedTripLegs(getSelectedTripLegsFromDataGrid());
    };

    const unsubRowSelection = dataGridApiRef.current?.subscribeEvent(
      'rowSelectionChange',
      handleRowSelectionChange,
    );

    return unsubRowSelection;
  }, [dataGridApiRef]);

  const handleFilterChange = (filter: string) => {
    // console.log('filter: ', filter);
  };

  const handlePaginationChange = (
    model: GridPaginationModel,
    _details: GridCallbackDetails,
  ) => {
    if (model.page !== allTripLegs.currentPage - 1) {
      dispatch(triplegSlice.setInventoryPage(model.page + 1));
    }
    if (model.pageSize !== pageSize) {
      dispatch(triplegSlice.setInventoryPageSize(model.pageSize));
    }
  };

  const handleSortModelChange = (
    model: GridSortModel,
    details: GridCallbackDetails<any>,
  ) => {
    let newModel: SortModel | null = null;
    if (model?.length) {
      if (model[0].field === 'pickUpDueTimeUTC') {
        newModel = {
          column: 'appointmentDate,pickUpDueTimeUTC',
          direction: model[0].sort === 'desc' ? 'desc,desc' : 'asc,asc',
        };
      } else {
        newModel = {
          column: model[0].field,
          direction: model[0].sort,
        };
      }
      dispatch(triplegSlice.setInventoryPage(1));
      dispatch(triplegSlice.setInventoryPageSortModel(newModel));
    }
  };

  const handleFilterDateChange = (date: Date | null) => {
    setFilterDate(date ? DateTime.fromJSDate(date) : null);
  };

  useEffect(() => {
    if (!tripLegEventEmitted) {
      return;
    }

    // Do not refetch on Open events here. There are far too many open events to process.
    if (tripLegEventEmitted.detailType === TripLegEventType.TripLegOpened) {
      return;
    }

    // If the event is on the current page, we need to refetch
    // to ensure the data is up to date regardless of filter or event
    const index = filteredTripLegs.findIndex(
      (tripLeg) =>
        tripLeg.tripLegReferenceID ===
        tripLegEventEmitted.detail.tripLegReferenceID,
    );
    if (index >= 0) {
      fetchTripLegs();
    }

    dispatch(triplegSlice.setTripLegEventEmitted(null));
  }, [
    tripLegEventEmitted,
    tripLegFilter,
    fetchTripLegs,
    dispatch,
    filteredTripLegs,
  ]);

  useEffect(() => {
    dispatch(lookupSlice.fetchLookupItemsAsync('passengerCareLevel'));
    dispatch(
      providerSlice.fetchProviderListAsync({
        pageSize,
        page,
        filter: 'ctt0&active0',
        search: '',
        sortModel: {
          column: 'transportationProviderName',
          direction: 'asc',
        },
      }),
    );
  }, []);

  return (
    <>
      <StyledBox elevation={2}>
        <Box className={classes.container}>
          <DataGridHeader>
            <DataGridTitle>{t('triplegInventory.title')}</DataGridTitle>

            <DataGridFilters>
              <DataGridSearch
                searchTerm={searchTerm}
                onSearchChange={handleSearchChange}
                placeholder={t('todaysTrips.searchPlaceholder')}
              />
              <FormControl className={classes.filterContainer}>
                <InputLabel id="trip-leg-inventory-filter-select">
                  {t('triplegInventory.filterStatus')}
                </InputLabel>
                <Select
                  labelId="trip-leg-inventory-filter-select"
                  value={
                    debouncedFilterTripStatus?.length ? 'custom' : tripLegFilter
                  }
                  disabled={debouncedFilterTripStatus?.length ? true : false}
                  onChange={handleTripLegFilterChange}>
                  {debouncedFilterTripStatus?.length ? (
                    <MenuItem value="custom">
                      {t('tripLegFilters.customFilter')}
                    </MenuItem>
                  ) : null}
                  <MenuItem value="unallocated">
                    {t('triplegInventory.filterUnallocatedOnly')}
                  </MenuItem>
                  <MenuItem value="allocated">
                    {t('triplegInventory.filterAllocatedOnly')}
                  </MenuItem>
                  <MenuItem value="inprogress">
                    {t('triplegInventory.filterInProgress')}
                  </MenuItem>
                  <MenuItem value="unattested">
                    {t('triplegInventory.filterUnattested')}
                  </MenuItem>
                  <MenuItem value="inactive">
                    {t('triplegInventory.filterInactive')}
                  </MenuItem>
                  <MenuItem value="showAll">
                    {t('triplegInventory.filterShowAll')}
                  </MenuItem>
                </Select>
              </FormControl>
              {/* <LocalizationProvider dateAdapter={AdapterLuxon}>
                <DateInput
                  name="filterDate"
                  control={control}
                  className={classes.datePickerContainer}
                  label={t('triplegInventory.date')}
                  defaultValue={filterDate}
                  clearable={true}
                  onChange={(updatedDate: DateTime | null) => {
                    handleFilterDateChange(
                      updatedDate ? updatedDate.toJSDate() : null,
                    );
                  }}
                />
              </LocalizationProvider> */}
              <DataGridFilterMenu
                label={t('todaysTrips.filters')}
                filters={filters}
                onFilterChange={handleFilterChange}
                control={control}
                resetField={(name: string) => resetField(name as any)}
                setValue={setValue}
              />
            </DataGridFilters>
          </DataGridHeader>
          <TripLegInventoryTable
            page={page}
            pageSize={pageSize}
            pageSizeOptions={[10, 20, 40, 50, 100]}
            rowCount={allTripLegs.itemCount}
            paginationChange={handlePaginationChange}
            sortModelChange={handleSortModelChange}
            tripLegs={filteredTripLegs}
            loading={allTripLegsLoading}
            showAssignAction={tripLegFilter === 'unallocated'}
            showReopenAction={tripLegFilter === 'unallocated'}
            showInProgress={tripLegFilter === 'inprogress'}
            showEditTimesAction={true}
            isSelectingTrips={isSelectingTrips}
            dataGridApiRef={dataGridApiRef}
          />
        </Box>
      </StyledBox>
      <Box sx={{ height: 24 }} />
      {selectedTripLegs.length > 0 && dialogPackageOpen && (
        <TripLegPackageDialog
          tripLegs={selectedTripLegs}
          open={dialogPackageOpen}
          close={closePackageDialog}
        />
      )}
    </>
  );
}
